import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheckCircle, FiXCircle, FiClock, FiCalendar, FiEye } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import Input from "../../components/input";
import NewTableComponent from "../../components/NewTableComponent";
import Stats from "../../components/Stats";
import UserAvatar from "../../components/UserAvatar";
import { useDispatch, useSelector } from "react-redux";
import DateFilterDropdown from "../../components/DateFilterDropdown";
import MonthFilterDropdown from "../../components/MonthFilterDropdown";
import SupervisorFilterDropdown from "../../components/SupervisorFilterDropdown";
import { fetchSupervisorsDropdown } from "../../store/Slices/dropdownSlice";
import { apiService } from "../../services/apiService";
import { useAttendanceListingExport } from "./useAttendanceListingExport";
import ExportButton from "../../components/ExportButton";
import "../../styles/Access.css";
import "../../styles/AssetsRegistration.css";
import "../../styles/Services.css";
import "../../styles/Table.css";
import "../../styles/DriverSafety.css";
import { errorNotify } from "../../utils/toast";

const getMonthDateRange = (monthStr = dayjs().format("YYYY-MM")) => {
  const startOfMonth = dayjs(`${monthStr}-01`);

  return {
    fromDate: startOfMonth.format("YYYY-MM-DD"),
    toDate: startOfMonth.endOf("month").format("YYYY-MM-DD"),
  };
};

const DEFAULT_DATE_RANGE = getMonthDateRange();

const emptyAttendanceSummary = {
  total_drivers: 0,
  total_present_days: 0,
  total_absences_leaves: 0,
};

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const toNumberMetric = (value, fallback = 0) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const normalizeAttendanceStatus = (status = "") => {
  const normalizedStatus = String(status || "").trim().toLowerCase().replace(/[\s_-]/g, "");

  if (normalizedStatus === "onleave" || normalizedStatus === "leave") return "On Leave";
  if (normalizedStatus === "present") return "Present";
  if (normalizedStatus === "absent") return "Absent";
  if (normalizedStatus === "late") return "Late";

  return status || "-";
};

const getDriverImage = (driverRecord) => driverRecord?.user?.image || "";

const getDriverName = (driverRecord) => driverRecord?.user?.username || driverRecord?.driverName || "-";

const getSupervisorName = (driverRecord) => driverRecord?.supervisor?.username || "-";

const getAssignedVehicle = (attendanceLogs = []) => {
  const vehicleLog = normalizeArray(attendanceLogs).find((log) => toNumberMetric(log?.vehicle_id, 0) > 0);
  const primaryVehicle = vehicleLog?.vehicle?.registration_number || vehicleLog?.vehicle?.vehicle_registration_number;

  if (primaryVehicle) return primaryVehicle;
  return vehicleLog?.vehicle_id ? String(vehicleLog.vehicle_id) : "-";
};

const mapAttendanceLog = (attendanceLog, driverRecord) => {
  const date = attendanceLog?.attendance_date ? dayjs(attendanceLog.attendance_date) : null;
  const dateValue = date?.isValid() ? date.format("YYYY-MM-DD") : "";

  return {
    id: attendanceLog?.id || `${driverRecord.id}-${dateValue || "attendance-log"}`,
    date: dateValue,
    dayName: date?.isValid() ? date.format("ddd") : "-",
    driverId: driverRecord.driverId || driverRecord.id,
    driverName: driverRecord.driverName,
    employeeNumber: driverRecord.employeeNumber,
    supervisorName: driverRecord.supervisorName,
    assignedVehicle:
      attendanceLog?.vehicle?.registration_number ||
      attendanceLog?.vehicle?.vehicle_registration_number ||
      (toNumberMetric(attendanceLog?.vehicle_id, 0) > 0 ? String(attendanceLog.vehicle_id) : driverRecord.assignedVehicle),
    image: driverRecord.image || "",
    status: normalizeAttendanceStatus(attendanceLog?.attendance_status),
    remarks: attendanceLog?.leave_reason || "-",
  };
};

const mapAttendanceDriver = (driverRecord) => {
  const attendanceLogs = normalizeArray(driverRecord?.attendance_logs);
  const attendanceSummary = driverRecord?.driver_attendance_summary || {};
  const presentDays = toNumberMetric(attendanceSummary.present_days_count ?? attendanceSummary.present_days);
  const absentDays = toNumberMetric(attendanceSummary.absent_days_count ?? attendanceSummary.absent_days);
  const leaveDays = toNumberMetric(attendanceSummary.leave_days_count ?? attendanceSummary.leave_days);
  const lateDays = toNumberMetric(attendanceSummary.late_days_count ?? attendanceSummary.late_days);
  const totalDays = presentDays + absentDays + leaveDays + lateDays;

  const mappedDriver = {
    raw: driverRecord,
    recordId: driverRecord?.id,
    userId: driverRecord?.user_id,
    id: driverRecord?.driver_info_id || (driverRecord?.id ? `DRV-${driverRecord.id}` : "-"),
    driverName: getDriverName(driverRecord),
    employeeNumber: driverRecord?.employee_id || "-",
    supervisorName: getSupervisorName(driverRecord),
    assignedVehicle: driverRecord?.vehicle_primary_driver?.registration_number || getAssignedVehicle(attendanceLogs),
    image: getDriverImage(driverRecord),
    totalDays,
    presentDays,
    lateDays,
    absentDays,
    leaveDays,
    rate: toNumberMetric(attendanceSummary.attendance_rate),
  };

  return {
    ...mappedDriver,
    attendanceLogs: attendanceLogs
      .map((attendanceLog) => mapAttendanceLog(attendanceLog, mappedDriver))
      .sort((firstLog, secondLog) => dayjs(firstLog.date).valueOf() - dayjs(secondLog.date).valueOf()),
  };
};

const mapDriverLogsResponse = (responseData = {}, fallbackDriver = null) => {
  const driverRecord = responseData?.driver || fallbackDriver?.raw || {};
  const logs = normalizeArray(responseData?.results);
  const summary = responseData?.summary || {};
  const presentDays = toNumberMetric(summary.present_days);
  const absentDays = toNumberMetric(summary.absent_days);
  const leaveDays = toNumberMetric(summary.leave_days);
  const totalMarkedDays = presentDays + absentDays + leaveDays;

  return mapAttendanceDriver({
    ...driverRecord,
    attendance_logs: logs,
    driver_attendance_summary: {
      present_days: presentDays,
      present_days_count: presentDays,
      absent_days: absentDays,
      absent_days_count: absentDays,
      leave_days: leaveDays,
      leave_days_count: leaveDays,
      attendance_rate: totalMarkedDays ? Math.floor((presentDays / totalMarkedDays) * 100) : 0,
    },
  });
};

const formatDisplayDate = (dateStr) => (dateStr ? dayjs(dateStr).format("DD MMM YYYY") : "-");

const getDateRangeLabel = (fromDate, toDate) => `${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}`;


const renderStatusBadge = (status) => {
  switch (status) {
    case "Present":
      return (
        <span
          style={{
            background: "#ECFDF5",
            color: "#059669",
            border: "1px solid #A7F3D0",
            borderRadius: "12px",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <FiCheckCircle size={13} /> Present
        </span>
      );
    case "Late":
      return (
        <span
          style={{
            background: "#FFFBEB",
            color: "#D97706",
            border: "1px solid #FCD34D",
            borderRadius: "12px",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <FiClock size={13} /> Late
        </span>
      );
    case "Absent":
      return (
        <span
          style={{
            background: "#FEF2F2",
            color: "#DC2626",
            border: "1px solid #FCA5A5",
            borderRadius: "12px",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <FiXCircle size={13} /> Absent
        </span>
      );
    case "On Leave":
      return (
        <span
          style={{
            background: "#EFF6FF",
            color: "#2563EB",
            border: "1px solid #93C5FD",
            borderRadius: "12px",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <FiCalendar size={13} /> On Leave
        </span>
      );
    default:
      return <span>{status}</span>;
  }
};

const AttendanceLogs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dateRange] = useState(DEFAULT_DATE_RANGE);
  const [selectedDriver, setSelectedDriver] = useState(null); // When non-null, shows daily logs view
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState(emptyAttendanceSummary);
  const [attendanceMeta, setAttendanceMeta] = useState({
    totalItems: 0,
    totalPages: 1,
  });
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [driverLogsRow, setDriverLogsRow] = useState(null);
  const [driverLogsMeta, setDriverLogsMeta] = useState({
    totalItems: 0,
    totalPages: 1,
  });
  const [driverLogsLoading, setDriverLogsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { fromDate, toDate } = dateRange;

  const handleDateRangeFilterChange = (updater) => {
    const next = typeof updater === "function" ? updater(searchFilters) : updater;
    // Mutual exclusivity: if dateRangeFilter was set, remove monthFilter; if monthFilter was set, it already removed dateRangeFilter internally
    if (next?.dateRangeFilter && next?.monthFilter) {
      const cleaned = { ...next };
      delete cleaned.monthFilter;
      setSearchFilters(cleaned);
      setCurrentPage(1);
      return;
    }
    setSearchFilters(next);
    setCurrentPage(1);
  };

  const { supervisorsDropdownData = [] } = useSelector(
    (state) => state.dropdowns || {}
  );

  useEffect(() => {
    dispatch(fetchSupervisorsDropdown());
  }, [dispatch]);

  const supervisorDropdownOptions = useMemo(() => {
    return (supervisorsDropdownData || []).map((supervisor) => ({
      label:
        supervisor?.label ||
        supervisor?.username ||
        supervisor?.name ||
        String(supervisor?.id ?? supervisor?.value ?? ""),
      value: supervisor?.value ?? supervisor?.id,
    }));
  }, [supervisorsDropdownData]);

  const {
    hasActiveFilters,
    exportLoading,
    pdfExportLoading,
    excelExportLoading,
    handleExportPdf,
    handleExportExcel,
  } = useAttendanceListingExport({
    searchText,
    searchFilters,
  });

  const dateRangeLabel = useMemo(() => {
    const monthFilter = searchFilters?.monthFilter?.value;
    if (monthFilter) {
      const range = getMonthDateRange(monthFilter);
      return getDateRangeLabel(range.fromDate, range.toDate);
    }
    const filterRange = searchFilters?.dateRangeFilter?.value;
    if (
      filterRange &&
      typeof filterRange === "object" &&
      filterRange.startDate &&
      filterRange.endDate
    ) {
      return getDateRangeLabel(filterRange.startDate, filterRange.endDate);
    }
    return getDateRangeLabel(fromDate, toDate);
  }, [searchFilters, fromDate, toDate]);

  useEffect(() => {
    if (selectedDriver) return undefined;

    let isActive = true;

    const fetchAttendanceSummary = async () => {
      setAttendanceLoading(true);

      try {
        const payload = {};
        if (searchText) {
          payload.search_text = searchText;
        }
        if (searchFilters && typeof searchFilters === "object") {
          Object.assign(payload, searchFilters);
        }
        // Convert monthFilter → dateRangeFilter before sending to backend
        if (payload.monthFilter) {
          const range = getMonthDateRange(payload.monthFilter.value);
          payload.dateRangeFilter = {
            filter_type: "date_range",
            value: { startDate: range.fromDate, endDate: range.toDate },
          };
          delete payload.monthFilter;
        }
        // Always send a date range so backend doesn't return all-time counts
        if (!payload.dateRangeFilter) {
          payload.dateRangeFilter = {
            filter_type: "date_range",
            value: { startDate: fromDate, endDate: toDate },
          };
        }

        const response = await apiService.getPaginatedAttendance(
          payload,
          {
            page: currentPage,
            limit,
          }
        );
        const responseData = response?.data || {};
        const responseMeta = responseData?.meta || {};
        const mappedRows = normalizeArray(responseData?.results).map(mapAttendanceDriver);
        const totalItems = toNumberMetric(responseMeta?.totalItems ?? responseData?.total, mappedRows.length);
        const totalPages = toNumberMetric(
          responseMeta?.totalPages,
          Math.max(1, Math.ceil(totalItems / Math.max(limit, 1)))
        );

        if (!isActive) return;

        setAttendanceRows(mappedRows);
        setAttendanceSummary({
          ...emptyAttendanceSummary,
          ...(responseData?.summary || {}),
        });
        setAttendanceMeta({
          totalItems,
          totalPages: Math.max(totalPages, 1),
        });
      } catch (error) {
        if (!isActive) return;

        setAttendanceRows([]);
        setAttendanceSummary(emptyAttendanceSummary);
        setAttendanceMeta({
          totalItems: 0,
          totalPages: 1,
        });
        errorNotify(error || "Failed to fetch attendance summary");
      } finally {
        if (isActive) {
          setAttendanceLoading(false);
        }
      }
    };

    fetchAttendanceSummary();

    return () => {
      isActive = false;
    };
  }, [currentPage, fromDate, limit, searchText, searchFilters, selectedDriver, toDate]);

  const summaryRows = attendanceRows;

  useEffect(() => {
    if (!selectedDriver) {
      setDriverLogsRow(null);
      setDriverLogsMeta({
        totalItems: 0,
        totalPages: 1,
      });
      setDriverLogsLoading(false);
      return undefined;
    }

    const driverId = selectedDriver.userId || selectedDriver.raw?.user_id || selectedDriver.raw?.user?.id;

    if (!driverId) {
      setDriverLogsRow(selectedDriver);
      setDriverLogsMeta({
        totalItems: 0,
        totalPages: 1,
      });
      errorNotify("Driver ID not found for attendance logs");
      return undefined;
    }

    let isActive = true;

    const fetchDriverLogs = async () => {
      setDriverLogsLoading(true);

      try {
        const monthFilter = searchFilters?.monthFilter?.value;
        let startDate, endDate;
        if (monthFilter) {
          const range = getMonthDateRange(monthFilter);
          startDate = range.fromDate;
          endDate = range.toDate;
        } else {
          const activeDateRange = searchFilters?.dateRangeFilter?.value;
          startDate = activeDateRange?.startDate
            ? activeDateRange.startDate.split("T")[0]
            : fromDate;
          endDate = activeDateRange?.endDate
            ? activeDateRange.endDate.split("T")[0]
            : toDate;
        }

        const response = await apiService.getDriverAttendanceLogs(
          {
            driver_id: driverId,
            start_date: startDate,
            end_date: endDate,
          },
          {
            page: currentPage,
            limit,
          }
        );
        const responseData = response?.data || {};
        const responseMeta = responseData?.meta || {};
        const mappedDriver = mapDriverLogsResponse(responseData, selectedDriver);
        const totalItems = toNumberMetric(responseMeta?.totalItems ?? responseData?.total, mappedDriver.attendanceLogs.length);
        const totalPages = toNumberMetric(
          responseMeta?.totalPages,
          Math.max(1, Math.ceil(totalItems / Math.max(limit, 1)))
        );

        if (!isActive) return;

        setDriverLogsRow(mappedDriver);
        setDriverLogsMeta({
          totalItems,
          totalPages: Math.max(totalPages, 1),
        });
      } catch (error) {
        if (!isActive) return;

        setDriverLogsRow({
          ...selectedDriver,
          attendanceLogs: [],
          presentDays: 0,
          absentDays: 0,
          leaveDays: 0,
        });
        setDriverLogsMeta({
          totalItems: 0,
          totalPages: 1,
        });
        errorNotify(error || "Failed to fetch driver attendance logs");
      } finally {
        if (isActive) {
          setDriverLogsLoading(false);
        }
      }
    };

    fetchDriverLogs();

    return () => {
      isActive = false;
    };
  }, [currentPage, fromDate, limit, searchFilters, selectedDriver, toDate]);

  const selectedDriverSummary = useMemo(() => {
    if (!selectedDriver) return null;
    return driverLogsRow || selectedDriver;
  }, [driverLogsRow, selectedDriver]);

  const handleViewDriverLogs = (driver) => {
    setSelectedDriver(driver);
    setDriverLogsRow(null);
    setDriverLogsMeta({
      totalItems: 0,
      totalPages: 1,
    });
    setCurrentPage(1);
    setSearchText("");
    // Preserve month/date-range filters — only clear text/row filters
    setSearchFilters((prev) => {
      if (!prev) return null;
      const preserved = {};
      if (prev.dateRangeFilter) preserved.dateRangeFilter = prev.dateRangeFilter;
      if (prev.monthFilter) preserved.monthFilter = prev.monthFilter;
      return Object.keys(preserved).length ? preserved : null;
    });
  };

  // Filtered Summary rows
  const filteredSummaryRows = useMemo(() => {
    return summaryRows.filter((item) => {
      if (searchFilters) {
        for (const [key, filterObj] of Object.entries(searchFilters)) {
          if (!filterObj || !filterObj.value) continue;
          const val = String(filterObj.value).toLowerCase().trim();
          const type = filterObj.filter_type || "contains";

          let itemVal = "";
          if (key === "driverIdFilter") itemVal = item.id;
          else if (key === "driverNameFilter") itemVal = item.driverName;
          else if (key === "employeeNumberFilter") itemVal = item.employeeNumber;
          else if (key === "supervisorFilter") itemVal = item.supervisorName;
          else if (key === "vehicleFilter") itemVal = item.assignedVehicle;

          itemVal = String(itemVal || "").toLowerCase();

          if (type === "contains" && !itemVal.includes(val)) return false;
          if (type === "equals" && itemVal !== val) return false;
        }
      }

      return true;
    });
  }, [searchFilters, summaryRows]);

  // Daily logs for selected driver
  const dailyLogs = useMemo(() => {
    if (!selectedDriver) return [];
    return normalizeArray(driverLogsRow?.attendanceLogs);
  }, [driverLogsRow, selectedDriver]);

  // Filtered daily logs
  const filteredDailyLogs = useMemo(() => {
    if (!selectedDriverSummary) return [];
    return dailyLogs.filter((item) => {
      if (searchText.trim()) {
        const q = searchText.toLowerCase().trim();
        const matchesDate = item.date.toLowerCase().includes(q);
        const matchesStatus = item.status.toLowerCase().includes(q);
        const matchesRemarks = item.remarks.toLowerCase().includes(q);
        const matchesDay = item.dayName.toLowerCase().includes(q);
        const matchesDriver = item.driverName.toLowerCase().includes(q);
        const matchesId = item.driverId.toLowerCase().includes(q);
        const matchesEmp = item.employeeNumber.toLowerCase().includes(q);
        const matchesSup = item.supervisorName.toLowerCase().includes(q);
        const matchesVeh = item.assignedVehicle.toLowerCase().includes(q);
        if (!(matchesDate || matchesStatus || matchesRemarks || matchesDay || matchesDriver || matchesId || matchesEmp || matchesSup || matchesVeh)) {
          return false;
        }
      }

      if (searchFilters) {
        for (const [key, filterObj] of Object.entries(searchFilters)) {
          if (!filterObj || !filterObj.value) continue;
          const val = String(filterObj.value).toLowerCase().trim();
          const type = filterObj.filter_type || "contains";

          let itemVal = "";
          if (key === "driverIdFilter") itemVal = item.driverId;
          else if (key === "driverNameFilter") itemVal = item.driverName;
          else if (key === "employeeNumberFilter") itemVal = item.employeeNumber;
          else if (key === "dateFilter") itemVal = item.date;
          else if (key === "dayNameFilter") itemVal = item.dayName;
          else if (key === "statusFilter") itemVal = item.status;
          else if (key === "supervisorFilter") itemVal = item.supervisorName;
          else if (key === "vehicleFilter") itemVal = item.assignedVehicle;
          else if (key === "remarksFilter") itemVal = item.remarks;

          itemVal = String(itemVal || "").toLowerCase();

          if (type === "contains" && !itemVal.includes(val)) return false;
          if (type === "equals" && itemVal !== val) return false;
        }
      }

      return true;
    });
  }, [dailyLogs, searchText, searchFilters, selectedDriverSummary]);

  // Stats cards for Main Summary View
  const mainStatsData = useMemo(() => {
    return [
      {
        title: "Total Drivers",
        value: toNumberMetric(attendanceSummary.total_drivers),
        text: dateRangeLabel,
      },
      {
        title: "Total Days Present",
        value: toNumberMetric(attendanceSummary.total_present_days),
        text: "Across all drivers",
      },
      {
        title: "Total Absences & Leaves",
        value: toNumberMetric(attendanceSummary.total_absences_leaves),
        text: "Across all drivers",
      },
    ];
  }, [attendanceSummary, dateRangeLabel]);

  // Columns for Summary Table View
  const summaryColumns = [
    {
      name: "Driver ID",
      key: "driver_id",
      selector: (row) => (
        <span
          className="hyperlink"
          role="button"
          tabIndex={0}
          onClick={() => handleViewDriverLogs(row)}
        >
          {row.id}
        </span>
      ),
      tableColumn: false,
      filterName: "driverIdFilter",
      type: "text",
      minWidth: "140px",
    },
    {
      name: "Driver Name",
      key: "driver_name",
      selector: (row) => (
        <div className="asset_driver_cell">
          <UserAvatar username={row.driverName} image={row.image || ""} marginRight="0px" />
          <span
            className="asset_driver_name hyperlink"
            role="button"
            onClick={() => handleViewDriverLogs(row)}
          >
            {row.driverName}
          </span>
        </div>
      ),
      tableColumn: false,
      filterName: "driverNameFilter",
      type: "text",
      minWidth: "220px",
    },
    {
      name: "Employee Number",
      key: "employee_number",
      selector: (row) => (
        <span className="hyperlink">{row.employeeNumber}</span>
      ),
      tableColumn: false,
      filterName: "employeeNumberFilter",
      type: "text",
      minWidth: "160px",
    },
    {
      name: "Supervisor",
      key: "supervisor",
      selector: (row) => row.supervisorName || "-",
      tableColumn: true,
      filterName: "supervisorIdFilter",
      type: "select",
      isMulti: true,
      filterComponent: SupervisorFilterDropdown,
      options: supervisorDropdownOptions,
      minWidth: "180px",
    },
    {
      name: "Vehicle",
      key: "vehicle",
      selector: (row) => <span className="hyperlink">{row.assignedVehicle}</span>,
      tableColumn: false,
      filterName: "vehicleFilter",
      type: "text",
      minWidth: "130px",
    },
    {
      name: "Month",
      key: "month_filter",
      selector: () => dateRangeLabel,
      tableColumn: true,
      filterName: "monthFilter",
      type: "select",
      filterComponent: MonthFilterDropdown,
      minWidth: "160px",
      omit: true,
    },
    {
      name: "Date Range",
      key: "date_range",
      selector: () => dateRangeLabel,
      tableColumn: true,
      filterName: "dateRangeFilter",
      type: "select",
      filterComponent: DateFilterDropdown,
      minWidth: "200px",
      omit: true,
    },
    {
      name: "Present",
      key: "present_days",
      selector: (row) => (
        <span
          style={{
            background: "#ECFDF5",
            color: "#059669",
            fontWeight: "700",
            padding: "4px 10px",
            borderRadius: "10px",
            fontSize: "13px",
          }}
        >
          {row.presentDays} days
        </span>
      ),
      minWidth: "130px",
    },
    {
      name: "Absent",
      key: "absent_days",
      selector: (row) => (
        <span
          style={{
            background: "#FEF2F2",
            color: "#DC2626",
            fontWeight: "700",
            padding: "4px 10px",
            borderRadius: "10px",
            fontSize: "13px",
          }}
        >
          {row.absentDays} days
        </span>
      ),
      minWidth: "120px",
    },
    {
      name: "On Leave",
      key: "leave_days",
      selector: (row) => (
        <span
          style={{
            background: "#EFF6FF",
            color: "#2563EB",
            fontWeight: "700",
            padding: "4px 10px",
            borderRadius: "10px",
            fontSize: "13px",
          }}
        >
          {row.leaveDays} days
        </span>
      ),
      minWidth: "120px",
    },
    {
      name: "Attendance Rate",
      key: "rate",
      selector: (row) => (
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: "50px",
              height: "6px",
              background: "#E5E7EB",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${row.rate}%`,
                height: "100%",
                background: row.rate >= 90 ? "#10B981" : "#F59E0B",
                borderRadius: "4px",
              }}
            />
          </div>
          <span style={{ fontWeight: "700", color: "#374151", fontSize: "13px" }}>
            {row.rate}%
          </span>
        </div>
      ),
      minWidth: "160px",
    },
    {
      name: "Actions",
      key: "actions",
      selector: (row) => (
        <button
          type="button"
          className="cancel_btn"
          onClick={() => handleViewDriverLogs(row)}
          style={{
            padding: "4px 12px",
            fontSize: "13px",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontWeight: "600",
          }}
        >
          <FiEye size={14} /> View Logs
        </button>
      ),
      minWidth: "140px",
    },
  ];

  // Columns for Selected Driver's Daily Logs View
  const dailyColumns = [
    {
      name: "Driver ID",
      key: "driver_id",
      selector: (row) => (
        <span className="hyperlink">{row.driverId || "-"}</span>
      ),
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Driver Name",
      key: "driver_name",
      selector: (row) => (
        <div className="asset_driver_cell">
          <UserAvatar username={row.driverName} image={row.image || ""} marginRight="0px" />
          <span className="asset_driver_name">{row.driverName || "-"}</span>
        </div>
      ),
      tableColumn: false,
      minWidth: "220px",
    },
    {
      name: "Employee Number",
      key: "employee_number",
      selector: (row) => (
        <span className="hyperlink">{row.employeeNumber || "-"}</span>
      ),
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Date",
      key: "date",
      selector: (row) => (
        <span style={{ fontWeight: "500", color: "#3D3F5C" }}>
          {formatDisplayDate(row.date)}
        </span>
      ),
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Day",
      key: "dayName",
      selector: (row) => (
        <span style={{ color: "#6B7280", fontWeight: "500" }}>{row.dayName}</span>
      ),
      tableColumn: false,
      minWidth: "110px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => renderStatusBadge(row.status),
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "Supervisor",
      key: "supervisor",
      selector: (row) => row.supervisorName || "-",
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Vehicle",
      key: "vehicle",
      selector: (row) => (
        <span className="hyperlink">{row.assignedVehicle || "-"}</span>
      ),
      tableColumn: false,
      minWidth: "130px",
    },
    {
      name: "Remarks",
      key: "remarks",
      selector: (row) => (
        <span style={{ fontSize: "13px", color: "#666", paddingRight: "24px", display: "inline-block" }}>
          {row.remarks || "-"}
        </span>
      ),
      tableColumn: true,
      filterName: "remarksFilter",
      type: "text",
      minWidth: "280px",
    },
    {
      name: "Month",
      key: "month_filter_daily",
      selector: () => dateRangeLabel,
      tableColumn: true,
      filterName: "monthFilter",
      type: "select",
      filterComponent: MonthFilterDropdown,
      minWidth: "160px",
      omit: true,
    },
    {
      name: "Date Range",
      key: "date_range_picker",
      selector: () => dateRangeLabel,
      tableColumn: true,
      filterName: "dateRangeFilter",
      type: "select",
      filterComponent: DateFilterDropdown,
      minWidth: "200px",
      omit: true,
    },
  ];

  const summaryTotalItems = attendanceMeta.totalItems;
  const summaryTotalPages = attendanceMeta.totalPages;
  const driverLogsTotalItems = driverLogsMeta.totalItems;
  const driverLogsTotalPages = driverLogsMeta.totalPages;

  return (
    <div className="access_page driver_safety_page attendance_logs_page">
      <Breadcrumbs
        values={
          selectedDriverSummary
            ? ["Operations", "Driver", "Attendance Logs", selectedDriverSummary.driverName]
            : ["Operations", "Driver", "Attendance Logs"]
        }
      />

      <div className="page_header access_view">
        <div>
          <h2>
            {selectedDriverSummary
              ? `${selectedDriverSummary.driverName}'s Attendance Logs`
              : "Driver Attendance Summary"}
          </h2>
          <p style={{ margin: "4px 0 0 0", color: "#666", fontSize: "13px" }}>
            {selectedDriverSummary
              ? `Daily breakdown from ${dateRangeLabel}`
              : `Attendance stats from ${dateRangeLabel}`}
          </p>
        </div>

        <div className="services_buttons attendance_logs_header_actions">

          {!selectedDriverSummary && (
            <>
              <ExportButton
                type="pdf"
                onClick={handleExportPdf}
                disabled={!hasActiveFilters || pdfExportLoading}
                loading={pdfExportLoading}
                hasActiveFilters={hasActiveFilters}
              />

              <ExportButton
                type="excel"
                onClick={handleExportExcel}
                disabled={!hasActiveFilters || excelExportLoading}
                loading={excelExportLoading}
                hasActiveFilters={hasActiveFilters}
              />
            </>
          )}

          <Button
            shouldRow={false}
            isShowSubmitBtn={false}
            cancelBtnText={selectedDriverSummary ? "Back to Summary" : "Back to Drivers"}
            cancelBtn={() => {
              if (selectedDriverSummary) {
                setSelectedDriver(null);
                setDriverLogsRow(null);
                setDriverLogsMeta({
                  totalItems: 0,
                  totalPages: 1,
                });
                setSearchText("");
                // Preserve month/date-range filters when going back to summary
                setSearchFilters((prev) => {
                  if (!prev) return null;
                  const preserved = {};
                  if (prev.dateRangeFilter) preserved.dateRangeFilter = prev.dateRangeFilter;
                  if (prev.monthFilter) preserved.monthFilter = prev.monthFilter;
                  return Object.keys(preserved).length ? preserved : null;
                });
                setCurrentPage(1);
                return;
              }

              navigate("/driver-safety");
            }}
            type="button"
          />
        </div>
      </div>

      {!selectedDriverSummary && <Stats statsData={mainStatsData} numberOfColumn={4} />}

      {selectedDriverSummary && (
        <div
          className="mb-2 py-3 px-4 bg-white d-flex align-items-center justify-content-between"
          style={{ background: "#F9FAFB" }}
        >
          <div className="d-flex align-items-center gap-3">
            <UserAvatar username={selectedDriverSummary.driverName} image={selectedDriverSummary.image || ""} marginRight="0px" />
            <div>
              <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#1F2937" }}>
                {selectedDriverSummary.driverName}
              </h5>
              <span style={{ fontSize: "13px", color: "#6B7280" }}>
                ID: {selectedDriverSummary.id} | Emp: {selectedDriverSummary.employeeNumber} | Supervisor: {selectedDriverSummary.supervisorName}
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span style={{ background: "#ECFDF5", color: "#059669", fontWeight: "700", padding: "6px 14px", borderRadius: "10px", fontSize: "13px" }}>
              Present: {selectedDriverSummary.presentDays} days
            </span>
            <span style={{ background: "#FEF2F2", color: "#DC2626", fontWeight: "700", padding: "6px 14px", borderRadius: "10px", fontSize: "13px" }}>
              Absent: {selectedDriverSummary.absentDays} days
            </span>
            <span style={{ background: "#EFF6FF", color: "#2563EB", fontWeight: "700", padding: "6px 14px", borderRadius: "10px", fontSize: "13px" }}>
              Leave: {selectedDriverSummary.leaveDays} days
            </span>
          </div>
        </div>
      )}

      <NewTableComponent
        tableClassName="parts_table_fix driver_status_table"
        showAddBtn={false}
        showFilter={true}
        columns={selectedDriverSummary ? dailyColumns : summaryColumns}
        data={selectedDriverSummary ? filteredDailyLogs : filteredSummaryRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={(nextLimit) => {
          setLimit(nextLimit);
          setCurrentPage(1);
        }}
        totalItems={selectedDriverSummary ? driverLogsTotalItems : summaryTotalItems}
        totalPages={selectedDriverSummary ? driverLogsTotalPages : summaryTotalPages}
        loading={selectedDriverSummary ? driverLogsLoading : attendanceLoading}
        searchValue={searchText}
        onSearch={(term) => {
          setSearchText(term);
          setCurrentPage(1);
        }}
        searchFilters={searchFilters}
        setSearchFilters={handleDateRangeFilterChange}
      />
    </div>
  );
};

export default AttendanceLogs;
